import { defineStore } from 'pinia';
import {
  ItemsCacheKeys,
  PreparedFolder,
  PreparedItem,
  PreparedWorkItem,
  UpdateItem,
} from '@/models/item.models';
import {
  GenerateItemDescriptionRequest,
  Relationship,
  SubmitBulkCreateItemsJob,
  UpdateFolder,
  UpdateSubItem,
  UpdateWorkItemRelationships,
} from '@/api/types.gen';
import uniqBy from 'lodash-es/uniqBy';
import { filtersToQueryParams, prepareWorkItems } from '@/store/helpers';
import { apiRequestHandler } from '@/utils/api';
import assignInWith from 'lodash-es/assignInWith';
import isArray from 'lodash-es/isArray';
import isNil from 'lodash-es/isNil';
import uniq from 'lodash-es/uniq';
import {
  CommentListResponse,
  CommentResponse,
  CreateComment,
  CreateItemComment,
  FindItemCommentQuery,
  FindItemsData,
  Folder,
  ItemType,
  GithubIntegrationService,
  UpdateComment,
  UpdateWorkItem,
  WorkItem,
  JobsService,
} from '@/api';
import { PaginatedQueryBase } from '@/models/common.models';
import { DataModule } from '@/modules/data.module';
import {
  CreateExportItemsJob,
  FindExportJobsQueryParams,
} from '@/models/data.models';
import { useOrganizationStore } from './organizationStore';
import { Branch, Commit, PullRequest } from '@/api';
import Notify from '@/utils/app-notification';
import {
  getLocalStorageKey,
  LocalStorageKeys,
  orgScopedKey,
  setLocalStorageKey,
} from '@/utils/local-storage';
import { AiModule } from '@/modules/ai.module';
import { LocalEvents } from './events';
import {
  AssetsService,
  CreateAssetGroup,
  CreateAssetItem,
  UpdateAssetGroup,
  UpdateAssetItem,
  CommentService,
  WorkType,
  FindActivityLogsData,
  ActivityService,
  ItemService,
  CopyWorkItem,
  CreateFolder,
  CreateSubItem,
  CreateWorkItem,
  FolderResponse,
  WorkItemResponse,
} from '@/api';
import { cloneDeep } from 'lodash-es';
import { useJobsStore } from '@/store/jobsStore';

export interface ItemState {
  itemsCaches: { [key: string]: Array<PreparedItem> }; // NOTE: arrays will be marked raw and will not be reactive. Always reassign the entire key to update.
  drawerOpen: boolean;
  lastRowFocused: string | null;
  lastColumnFocused: string | null;
  lastFocusedSubtaskId: string | null;
  isFocusInSubtask: boolean;
  lastParentColumnBeforeSubtask: string | null;
  alphaNumericInput: string | null;
  isSideBarComponentFocused: boolean;
  triggerItemTableCellFocus: boolean;
  drawerLoadingMap: { [key: PreparedItem['number']]: boolean };
  workspaceItemNumbers: Array<string>;
  scrollToDrawerComments: boolean;
  addBlocker: {
    visible: boolean;
    workItem: PreparedWorkItem | null;
  };
  drawerOpenAnimationDisabled: boolean;
  recentItems: Array<PreparedItem>;
  addItemOrFolderExecutedOnce: boolean;
  descriptionImageUploading: boolean;
  updateItemWorkTypeModal: {
    visible: boolean;
    updating: boolean;
    items: PreparedWorkItem[];
    newWorkTypeId: string | null;
    updateOverrideFunction:
      | ((data: {
          workType: WorkType;
          forcedParentNumber: string;
        }) => Promise<void>)
      | null;
    /** Callback invoked when the modal completes (success=true) or is cancelled (success=false) */
    onComplete: ((success: boolean) => void) | null;
  };
  itemTypeAggregationsModal: {
    visible: boolean;
    item: PreparedItem | null;
  };
  relationshipGraphItems: Array<PreparedItem>;
}

export const useItemStore = defineStore('item', {
  /****
   STATE
   ****/
  state: (): ItemState => {
    return {
      itemsCaches: {},
      drawerOpen: false,
      lastRowFocused: null,
      lastColumnFocused: null,
      lastFocusedSubtaskId: null,
      isFocusInSubtask: false,
      lastParentColumnBeforeSubtask: null,
      alphaNumericInput: null,
      isSideBarComponentFocused: false,
      triggerItemTableCellFocus: false,
      drawerLoadingMap: {},
      workspaceItemNumbers: [],
      scrollToDrawerComments: false,
      addBlocker: {
        visible: false,
        workItem: null,
      },
      drawerOpenAnimationDisabled: false,
      recentItems: [],
      addItemOrFolderExecutedOnce: false,
      descriptionImageUploading: false,
      updateItemWorkTypeModal: {
        visible: false,
        updating: false,
        items: [],
        newWorkTypeId: null,
        updateOverrideFunction: null,
        onComplete: null,
      },
      itemTypeAggregationsModal: {
        visible: false,
        item: null,
      },
      relationshipGraphItems: [],
    };
  },

  /****
   GETTERS
   ****/
  getters: {
    lastDrawerWorkItem(state) {
      if (!state.workspaceItemNumbers.length) return null;
      return (
        state.itemsCaches[ItemsCacheKeys.DRAWER_ITEMS]?.find(
          (item) =>
            item.number ===
            state.workspaceItemNumbers[state.workspaceItemNumbers.length - 1]
        ) || null
      );
    },
    rootFolders(state): Array<PreparedFolder> {
      return (state.itemsCaches[ItemsCacheKeys.ROOT_FOLDERS]
        ?.filter(
          (item) => item.itemType === ItemType.FOLDER && !item.parentNumber
        )
        ?.sort((a, b) =>
          a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1
        ) || []) as Array<PreparedFolder>;
    },
  },

  /****
   ACTIONS
   ****/
  actions: {
    generateItemDescription(data: GenerateItemDescriptionRequest) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const aiModule = new AiModule(organizationId);
        const response = await aiModule.generateItemDescription(data);
        return response.data.html;
      });
    },
    setIAddItemOrFolderExecutedOnce() {
      this.addItemOrFolderExecutedOnce = true;
    },
    setDescriptionImageUploading(value: boolean) {
      this.descriptionImageUploading = value;
    },
    setUpdateItemWorkTypeModalData(data: {
      visible?: boolean;
      updating?: boolean;
      items?: PreparedWorkItem[];
      newWorkTypeId?: string | null;
      updateOverrideFunction?:
        | ((data: {
            workType: WorkType;
            forcedParentNumber: string;
          }) => Promise<void>)
        | null;
      onComplete?: ((success: boolean) => void) | null;
    }) {
      this.updateItemWorkTypeModal = {
        ...this.updateItemWorkTypeModal,
        ...data,
      };
    },
    resetUpdateItemWorkTypeModalData() {
      // Note: The onComplete callback is called by WorkTypeChangeConfirmation.vue
      // before calling this reset, so we don't need to call it here
      this.updateItemWorkTypeModal = {
        visible: false,
        updating: false,
        items: [],
        newWorkTypeId: null,
        updateOverrideFunction: null,
        onComplete: null,
      };
    },
    setItemTypeAggregationsModalData(data: {
      visible?: boolean;
      item?: PreparedItem | null;
    }) {
      this.itemTypeAggregationsModal = {
        ...this.itemTypeAggregationsModal,
        ...data,
      };
    },
    resetItemTypeAggregationsModalData() {
      this.itemTypeAggregationsModal = {
        visible: false,
        item: null,
      };
    },
    setItems({ items, key }: { items: Array<PreparedItem>; key: string }) {
      this.itemsCaches[key] = uniqBy(
        items.slice().reverse(), // Reverse here so the newest items are on top
        'id'
      );
    },
    removeItems({
      itemIds,
      keys,
    }: {
      itemIds: Array<string>;
      keys?: Array<string>;
    }) {
      keys = keys || Object.keys(this.itemsCaches);
      keys.forEach((key) => {
        if (!this.itemsCaches[key]) {
          return;
        }
        this.itemsCaches[key] = this.itemsCaches[key].filter(
          (item) => !itemIds.includes(item.id) && !itemIds.includes(item.number)
        );
      });
    },
    clearItems(key: string) {
      // Delete the key entirely to break all references, not just empty the array
      delete this.itemsCaches[key];
    },
    toggleSideDrawerOpen(value: boolean) {
      this.drawerOpen = value;
    },
    setLastRowFocused(value: string) {
      this.lastRowFocused = value;
    },
    setLastColumnFocused(value: string) {
      this.lastColumnFocused = value;
    },
    setLastFocusedSubtaskId(value: string | null) {
      this.lastFocusedSubtaskId = value;
    },
    setIsFocusInSubtask(value: boolean) {
      this.isFocusInSubtask = value;
    },
    setLastParentColumnBeforeSubtask(value: string | null) {
      this.lastParentColumnBeforeSubtask = value;
    },
    setAlphaNumericInput(value: string) {
      this.alphaNumericInput = value;
    },
    clearAlphaNumericInput() {
      this.alphaNumericInput = null;
    },
    setIsSideBarComponentFocused(value: boolean) {
      this.isSideBarComponentFocused = value;
    },
    setTriggerItemTableCellFocus(value: boolean) {
      this.triggerItemTableCellFocus = value;
    },
    toggleScrollToDrawerComments(value: boolean) {
      this.scrollToDrawerComments = value;
    },
    toggleSideDrawerLoading(number: string, value: boolean) {
      this.drawerLoadingMap = {
        ...this.drawerLoadingMap,
        [number]: value,
      };
    },
    toggleSideDrawerAnimationDisabled(value: boolean) {
      this.drawerOpenAnimationDisabled = value;
    },
    popWorkspaceItemNumbers() {
      this.workspaceItemNumbers.pop();
    },
    clear() {
      this.drawerLoadingMap = {};
      this.drawerOpen = false;
      this.workspaceItemNumbers = [];
      this.scrollToDrawerComments = false;
    },

    toggleAddBlocker(options: {
      visible?: boolean;
      workItem?: PreparedWorkItem;
    }) {
      this.addBlocker = Object.assign(this.addBlocker, options);
    },
    addItems({
      items,
      disableAdding,
      keys,
    }: {
      items: Array<PreparedItem>;
      disableAdding?: boolean;
      keys?: Array<string>;
    }) {
      const updatedWorkItemNumbers = new Set<PreparedItem['number']>();
      // this array will have a the combined changes and workitems will always be unique
      const combinedChangesMap = new Map<
        PreparedItem['number'],
        PreparedItem
      >();

      function customizer(objValue: unknown, srcValue: unknown) {
        if (isArray(objValue)) {
          return srcValue;
        }
      }

      items.forEach((item) => {
        const existingChange = combinedChangesMap.get(item.number);
        if (existingChange) {
          combinedChangesMap.set(
            item.number,
            assignInWith(existingChange, item, customizer)
          );
        } else {
          combinedChangesMap.set(item.number, item);
        }
      });

      keys = keys && keys.length ? keys : Object.keys(this.itemsCaches);
      keys.forEach((key) => {
        if (!this.itemsCaches[key] && disableAdding) {
          return;
        }

        const newCacheItems = (this.itemsCaches[key] || []).map((oldItem) => {
          const item = combinedChangesMap.get(oldItem.number);
          if (item) {
            updatedWorkItemNumbers.add(item.number);
          }

          // common properties
          if (item) {
            // if the parent number property is missing
            if (!item.parentNumber) {
              item.parentNumber = undefined;
            }

            if (isNil(item.manualPercentCompleted)) {
              item.manualPercentCompleted = undefined;
            }
          }

          // workitem specific properties
          if (item && item.itemType === ItemType.WORKITEM) {
            if (!item.teamId) {
              item.teamId = undefined;
            }

            // if the assignee username property is missing
            if (!item.assigneeUsername) {
              item.assigneeUsername = undefined;
              item.assignee = undefined;
              item.assignedAt = undefined;
            }

            // if the board id property is missing
            if (!item.boardId) {
              item.boardId = undefined;
              item.boardRank = undefined;
            }

            if (!item.completedAt) {
              item.completedAt = undefined;
              item.completedByUser = undefined;
            }
          }

          // if (oldItem.parents) {
          //   // check if a parent of an item changed.
          //   oldItem.parents = oldItem.parents.map((oldItem) => {
          //     const item = combinedChanges.find(
          //       (newItem) => newItem.number === oldItem.number
          //     );
          //     return assignInWith(oldItem, item || {}, customizer);
          //   });
          // }
          return item ? assignInWith(oldItem, item, customizer) : oldItem;
        });

        this.setItems({
          items: newCacheItems,
          key,
        });
        if (!disableAdding) {
          const itemsToAdd = items.filter(
            (i) => !updatedWorkItemNumbers.has(i.number)
          );
          // Initialize cache if it doesn't exist (may have been cleared)
          if (!this.itemsCaches[key]) {
            this.itemsCaches[key] = [];
          }
          this.itemsCaches[key] = uniqBy(
            [...itemsToAdd.slice().reverse(), ...this.itemsCaches[key]],
            'id'
          );
        }
      });
    },

    findItemInCache({
      itemNumber,
      cacheKeyPattern,
    }: {
      itemNumber: string;
      cacheKeyPattern?: string;
    }) {
      const cacheKeys = Object.keys(this.itemsCaches);
      for (const cacheKey of cacheKeys) {
        if (cacheKeyPattern && !cacheKey.includes(cacheKeyPattern)) {
          continue;
        }
        const item = this.itemsCaches[cacheKey].find(
          (w: PreparedItem) => w.number === itemNumber
        );
        if (item) return item;
      }
      return null;
    },

    async loadBoardWorkItems({
      boardId,
      key,
      query,
    }: {
      boardId: string;
      key?: string;
      query?: FindItemsData['query'];
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await ItemService.findItems<true>({
          path: { organizationId },
          query: filtersToQueryParams({
            limit: 10000,
            ...query,
            boardId: [boardId],
            itemType: ItemType.WORKITEM,
          }) as unknown as Parameters<typeof ItemService.findItems>[0]['query'],
        });
        const items = await prepareWorkItems(response.data.data);
        this.addItems({
          items,
          keys: key ? [key] : undefined,
        });
        return response.data;
      });
    },

    async search({
      query = {},
      key,
    }: {
      query: FindItemsData['query'];
      key?: string;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId || !query)
        return {
          items: [] as PreparedItem[],
        };
      const response = await ItemService.findItems<true>({
        path: { organizationId },
        query: filtersToQueryParams({
          ...query,
          direction: query?.direction || 'desc',
          limit: query?.limit ?? 50,
        }) as unknown as Parameters<typeof ItemService.findItems>[0]['query'],
      });
      const items = await prepareWorkItems(response.data.data);
      if (key) {
        this.addItems({
          items,
          keys: [key],
        });
      }

      return { items, response: response.data };
    },

    async searchRaw({ query }: { query: FindItemsData['query'] }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId)
        return {
          items: [] as (WorkItem | Folder)[],
        };
      const response = await ItemService.findItems<true>({
        path: { organizationId },
        query: filtersToQueryParams({
          ...query,
          direction: query?.direction || 'desc',
          limit: query?.limit ?? 50,
        }) as unknown as Parameters<typeof ItemService.findItems>[0]['query'],
      });
      return { items: response.data.data, response };
    },

    async loadAllRootFolders() {
      const payload: FindItemsData['query'] = {
        itemType: ItemType.FOLDER,
        limit: 10000,
        hasNoParent: true,
      };
      return this.search({
        query: payload,
        key: ItemsCacheKeys.ROOT_FOLDERS,
      });
    },

    async updateWorkItem(params: {
      workItemNumber: string;
      itemType: ItemType;
      data: UpdateItem;
      keyPatternsToAddTo?: string[];
      revertCallback?: () => void;
      successCallback?: (response: FolderResponse | WorkItemResponse) => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;

      return apiRequestHandler(
        async () => {
          const cacheKeys = Object.keys(this.itemsCaches);
          const oldWorkItem = (() => {
            for (const cacheKey of cacheKeys) {
              const item = this.itemsCaches[cacheKey].find(
                (w: PreparedItem) => w.number === params.workItemNumber
              );
              if (item) return item;
            }
            return null;
          })();

          let response: FolderResponse | WorkItemResponse;
          if (params.itemType === ItemType.FOLDER) {
            response = (
              await ItemService.updateFolder<true>({
                path: { organizationId, itemNumber: params.workItemNumber },
                body: params.data as UpdateFolder,
              })
            ).data;
          } else {
            response = (
              await ItemService.updateWorkItem<true>({
                path: { organizationId, itemNumber: params.workItemNumber },
                body: params.data as UpdateWorkItem,
              })
            ).data;
          }
          const item = response.data;

          const updateWorkItems = [...(await prepareWorkItems([item]))];
          for (const cacheKey of cacheKeys) {
            if (params.data.parentNumber) {
              if (oldWorkItem && oldWorkItem.parentNumber) {
                const oldParent = this.itemsCaches[cacheKey].find(
                  (w: PreparedItem) => w.number === oldWorkItem.parentNumber
                );
                if (oldParent) {
                  const existingUpdatedParentIndex = updateWorkItems.findIndex(
                    (i) => i.number === oldParent.number
                  );
                  let childCountUpdate: Partial<
                    Pick<
                      PreparedFolder,
                      'archivedItemCount' | 'itemCount' | 'folderCount'
                    >
                  > = {};
                  if (item.itemType === ItemType.WORKITEM) {
                    if (item.archived) {
                      childCountUpdate = {
                        archivedItemCount:
                          (oldParent as PreparedFolder).archivedItemCount - 1,
                      };
                    } else {
                      childCountUpdate = {
                        itemCount: (oldParent as PreparedFolder).itemCount - 1,
                      };
                    }
                  } else {
                    childCountUpdate = {
                      folderCount:
                        (oldParent as PreparedFolder).folderCount - 1,
                    };
                  }
                  if (existingUpdatedParentIndex > -1) {
                    updateWorkItems[existingUpdatedParentIndex] = {
                      ...updateWorkItems[existingUpdatedParentIndex],
                      ...childCountUpdate,
                    };
                  }
                }
              }

              const newParent = this.itemsCaches[cacheKey].find(
                (w: PreparedItem) => w.number === params.data.parentNumber
              );
              if (newParent) {
                const existingUpdatedParentIndex = updateWorkItems.findIndex(
                  (i) => i.number === newParent.number
                );
                let childCountUpdate: Partial<
                  Pick<
                    PreparedFolder,
                    'archivedItemCount' | 'itemCount' | 'folderCount'
                  >
                > = {};
                if (item.itemType === ItemType.WORKITEM) {
                  if (item.archived) {
                    childCountUpdate = {
                      archivedItemCount:
                        (newParent as PreparedFolder).archivedItemCount - 1,
                    };
                  } else {
                    childCountUpdate = {
                      itemCount: (newParent as PreparedFolder).itemCount - 1,
                    };
                  }
                } else {
                  childCountUpdate = {
                    folderCount: (newParent as PreparedFolder).folderCount + 1,
                  };
                }
                if (existingUpdatedParentIndex > -1) {
                  updateWorkItems[existingUpdatedParentIndex] = {
                    ...updateWorkItems[existingUpdatedParentIndex],
                    ...childCountUpdate,
                  };
                }
              }
            }

            // make sure that if the cycle was changed or added it will show up on the plan > items tab && on the board
            if ((item as WorkItem).boardId) {
              this.addItems({
                items: updateWorkItems,
                keys: [ItemsCacheKeys.BOARD_ITEMS],
              });
            }

            if (!params.keyPatternsToAddTo) {
              this.addItems({
                items: updateWorkItems,
                disableAdding: true,
                keys: cacheKeys,
              });
            } else {
              cacheKeys.forEach((key) => {
                const patternMatchesKey = params.keyPatternsToAddTo?.some(
                  (pattern) => key.includes(pattern)
                );
                if (patternMatchesKey) {
                  this.addItems({
                    items: updateWorkItems,
                    keys: [key],
                  });
                } else {
                  this.addItems({
                    items: updateWorkItems,
                    disableAdding: true,
                    keys: [key],
                  });
                }
              });
            }
          }

          params.successCallback?.(response);

          if (oldWorkItem) {
            LocalEvents.emit('item-updated', updateWorkItems[0]);
          }
          return response;
        },
        {
          errorCallback: params.revertCallback,
        }
      );
    },

    async updateItemRelationships(params: {
      workItemNumber: string;
      itemType: ItemType;
      data: UpdateWorkItemRelationships;
      keyPatternsToAddTo?: string[];
      revertCallback?: () => void;
      successCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;

      // get a copy of the item in its current state so we can account for any removed relationships
      const oldRelationshipState = cloneDeep(
        this.findItemInCache({
          itemNumber: params.workItemNumber,
        })?.relationships
      );

      const refreshImpactedItems = async () => {
        const relationshipsToItemNumbers = (relationships: Relationship[]) => {
          return relationships
            .filter((r) => r.entity === 'folder' || r.entity === 'workitem')
            .map((r) => r.referenceId);
        };

        const impactedItemNumbers: PreparedItem['number'][] = uniq([
          ...relationshipsToItemNumbers(oldRelationshipState || []),
          ...relationshipsToItemNumbers(params.data.relationships || []),
          ...relationshipsToItemNumbers(params.data.addedRelationships || []),
          ...relationshipsToItemNumbers(params.data.removedRelationships || []),
        ]);

        const itemNumbersToRefresh = impactedItemNumbers.filter((number) => {
          const isLoaded = !!this.loadFromCache(number);
          return isLoaded; // it's a loaded item, so we need to refresh it
        });
        const workItemNumbers = itemNumbersToRefresh.filter((n) =>
          n.startsWith('I')
        );
        const folderNumbers = itemNumbersToRefresh.filter((n) =>
          n.startsWith('F')
        );

        const pWorkItems = workItemNumbers.length
          ? this.search({
              query: {
                number: workItemNumbers,
                itemType: ItemType.WORKITEM,
                limit: workItemNumbers.length,
              },
            })
          : Promise.resolve({ items: [] });

        const pFolders = folderNumbers.length
          ? this.search({
              query: {
                number: folderNumbers,
                itemType: ItemType.FOLDER,
                limit: folderNumbers.length,
              },
            })
          : Promise.resolve({ items: [] });

        const loadedItems = [
          ...(await pWorkItems).items,
          ...(await pFolders).items,
        ];
        this.addItems({ items: loadedItems });

        return loadedItems;
      };

      return apiRequestHandler(
        async () => {
          const cacheKeys = Object.keys(this.itemsCaches);

          let response: FolderResponse | WorkItemResponse;
          if (params.itemType === ItemType.FOLDER) {
            response = (
              await ItemService.updateFolderRelationships<true>({
                path: { organizationId, itemNumber: params.workItemNumber },
                body: params.data,
              })
            ).data;
          } else {
            response = (
              await ItemService.updateWorkItemRelationships<true>({
                path: { organizationId, itemNumber: params.workItemNumber },
                body: params.data,
              })
            ).data;
          }
          const item = response.data;

          const updateWorkItems = [...(await prepareWorkItems([item]))];

          if (!params.keyPatternsToAddTo) {
            this.addItems({
              items: updateWorkItems,
              disableAdding: true,
              keys: cacheKeys,
            });
          } else {
            cacheKeys.forEach((key) => {
              const patternMatchesKey = params.keyPatternsToAddTo?.some(
                (pattern) => key.includes(pattern)
              );
              if (patternMatchesKey) {
                this.addItems({
                  items: updateWorkItems,
                  keys: [key],
                });
              } else {
                this.addItems({
                  items: updateWorkItems,
                  disableAdding: true,
                  keys: [key],
                });
              }
            });
          }

          params.successCallback?.();
          refreshImpactedItems(); // refresh in the background
          return response;
        },
        {
          errorCallback: params.revertCallback,
        }
      );
    },

    async bulkUpdateItems(params: {
      items: Array<PreparedFolder | PreparedWorkItem>;
      data: UpdateWorkItem | UpdateFolder;
      addedLabelIds?: string[];
      removedLabelIds?: string[];
      addedRelationships?: Array<Relationship>;
      removedRelationships?: Array<Relationship>;
      keyPatternsToAddTo?: string[];
      revertCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;

      return apiRequestHandler(
        async () => {
          const cacheKeys = Object.keys(this.itemsCaches);
          const oldWorkItems: Array<PreparedItem> = [];
          for (const item of params.items) {
            for (const cacheKey of cacheKeys) {
              const oldItem = this.itemsCaches[cacheKey].find(
                (w: PreparedItem) => w.number === item.number
              );
              if (oldItem) oldWorkItems.push(oldItem);
            }
          }

          let workItems: Array<PreparedWorkItem> = params.items.filter(
            (item) => item.itemType === ItemType.WORKITEM
          ) as Array<PreparedWorkItem>;
          let folders: Array<PreparedFolder> = params.items.filter(
            (item) => item.itemType === ItemType.FOLDER
          ) as Array<PreparedFolder>;

          if (params.addedLabelIds?.length) {
            workItems = workItems.map((item) => ({
              ...item,
              labels: uniq([
                ...((item as PreparedWorkItem).labels || []),
                ...(params.addedLabelIds as string[]),
              ]),
            }));
          }

          if (params.addedRelationships?.length) {
            workItems = workItems.map((item) => ({
              ...item,
              relationships: [
                ...item.relationships,
                ...(params.addedRelationships || []),
              ],
            }));
            folders = folders.map((item) => ({
              ...item,
              relationships: [
                ...item.relationships,
                ...(params.addedRelationships || []),
              ],
            }));
          }
          if (params.removedRelationships?.length) {
            workItems = workItems.map((item) => ({
              ...item,
              relationships: item.relationships.filter(
                (relationship) =>
                  !params.removedRelationships?.some(
                    (r) =>
                      r.referenceId === relationship.referenceId &&
                      r.type === relationship.type
                  )
              ),
            }));
            folders = folders.map((item) => ({
              ...item,
              relationships: item.relationships.filter(
                (relationship) =>
                  !params.removedRelationships?.some(
                    (r) =>
                      r.referenceId === relationship.referenceId &&
                      r.type === relationship.type
                  )
              ),
            }));
          }

          if (params.removedLabelIds?.length) {
            workItems = workItems.map((item) => ({
              ...item,
              labels: ((item as PreparedWorkItem).labels || []).filter(
                (label) => !(params.removedLabelIds as string[]).includes(label)
              ),
            }));
          }

          const requestWorkItems = workItems.map((item) => {
            const workItem = {
              number: item.number,
              relationships: item.relationships,
              ...params.data,
              ...(params.addedLabelIds?.length || params.removedLabelIds?.length
                ? {
                    labels: (item as PreparedWorkItem).labels,
                  }
                : {}),
              ...(params.addedRelationships?.length ||
              params.removedRelationships?.length
                ? {
                    relationships: (item as PreparedWorkItem).relationships,
                  }
                : {}),
            };
            return {
              ...workItem,
              parentNumber:
                workItem.parentNumber === null
                  ? undefined
                  : workItem.parentNumber,
            };
          });
          const requestFolders = folders.map((item) => ({
            number: item.number,
            relationships: item.relationships,
            ...params.data,
          }));

          let items: Array<WorkItem | Folder> = [];
          if (
            params.data.parentNumber ||
            (params.data as UpdateWorkItem).teamId ||
            (params.data as UpdateWorkItem).boardId ||
            (params.data as UpdateWorkItem).laneId
          ) {
            const response = await ItemService.bulkUpdateItems<true>({
              path: { organizationId },
              body: {
                workItems: requestWorkItems,
                folders: requestFolders,
              },
            });
            items = [...response.data.workItems, ...response.data.folders];
            if (response.data.errors.length) {
              const failedCount = response.data.errors.length;
              const successCount = items.length;
              const allItems = [...workItems, ...folders];
              const failedItemDescriptions = response.data.errors
                .map((e: { itemId: string; error: string }) => {
                  const itemNumber = e.itemId.split(':')[1];
                  const item = allItems.find((i) => i.number === itemNumber);
                  return item ? `${item.number} - ${item.title}` : itemNumber;
                })
                .join(', ');
              const errorMessage =
                response.data.errors[0]?.error || 'Unknown error';

              throw new Error(
                `Failed to update ${failedCount} item(s): ${failedItemDescriptions}. ` +
                  `${
                    successCount > 0
                      ? `${successCount} item(s) updated successfully. `
                      : ''
                  }` +
                  `Error: ${errorMessage}`
              );
            }
          } else if (
            params.removedRelationships?.length ||
            params.addedRelationships?.length
          ) {
            const numberItemsMap = [...workItems, ...folders].reduce(
              (acc, item) => {
                acc[item.number] = item;
                return acc;
              },
              {} as Record<string, PreparedWorkItem | PreparedFolder>
            );
            await Promise.allSettled([
              [...requestFolders, ...requestWorkItems].map((item) => {
                return this.updateItemRelationships({
                  workItemNumber: item.number,
                  itemType: numberItemsMap[item.number].itemType,
                  data: {
                    relationships: item.relationships,
                    staticMode: false,
                  },
                });
              }),
            ]);
          } else {
            const itemUpdateResponses = await Promise.allSettled([
              ...requestWorkItems.map((item) => {
                return ItemService.updateWorkItem<true>({
                  path: { organizationId, itemNumber: item.number },
                  body: item,
                });
              }),
              ...requestFolders.map((item) => {
                return ItemService.updateFolder<true>({
                  path: { organizationId, itemNumber: item.number },
                  body: item,
                });
              }),
            ]);

            items = itemUpdateResponses
              .filter((i) => i.status === 'fulfilled')
              .map((i) => i.value.data.data);

            const failedUpdates = itemUpdateResponses.filter(
              (i): i is PromiseRejectedResult => i.status === 'rejected'
            );

            if (failedUpdates.length > 0) {
              const allItems = [...workItems, ...folders];
              const failedRequestItems = [
                ...requestWorkItems,
                ...requestFolders,
              ].filter(
                (_, index) => itemUpdateResponses[index].status === 'rejected'
              );
              const failedItemDescriptions = failedRequestItems
                .map((reqItem) => {
                  const item = allItems.find(
                    (i) => i.number === reqItem.number
                  );
                  return item
                    ? `${item.number} - ${item.title}`
                    : reqItem.number;
                })
                .join(', ');
              const successCount =
                itemUpdateResponses.length - failedUpdates.length;
              // Extract error message from various possible error structures
              const firstError = failedUpdates[0]?.reason;
              const errorMessage =
                firstError?.body?.message ||
                firstError?.message ||
                firstError?.response?.data?.message ||
                (typeof firstError === 'string' ? firstError : 'Unknown error');

              // Log full error for debugging
              console.error(
                '[itemStore.bulkUpdateItems] Failed updates:',
                failedUpdates.map((f) => f.reason)
              );

              throw new Error(
                `Failed to update ${failedUpdates.length} item(s): ${failedItemDescriptions}. ` +
                  `${
                    successCount > 0
                      ? `${successCount} item(s) updated successfully. `
                      : ''
                  }` +
                  `Error: ${errorMessage}`
              );
            }
          }

          const updateWorkItems = [...(await prepareWorkItems(items))];

          for (const item of items) {
            if (params.data.parentNumber) {
              const oldWorkItem = oldWorkItems.find(
                (w: PreparedItem) => w.number === item.number
              );
              if (oldWorkItem && oldWorkItem.parentNumber) {
                const oldParent = cacheKeys
                  .map((key) =>
                    this.itemsCaches[key].find(
                      (w: PreparedItem) => w.number === oldWorkItem.parentNumber
                    )
                  )
                  .find((i) => i);
                if (oldParent) {
                  const existingUpdatedParentIndex = updateWorkItems.findIndex(
                    (i) => i.number === oldParent.number
                  );
                  let childCountUpdate: Partial<
                    Pick<
                      PreparedFolder,
                      'archivedItemCount' | 'itemCount' | 'folderCount'
                    >
                  > = {};
                  if (existingUpdatedParentIndex !== -1) {
                    const existingUpdatedParent = updateWorkItems[
                      existingUpdatedParentIndex
                    ] as PreparedFolder;
                    if (item.itemType === ItemType.WORKITEM) {
                      if (item.archived) {
                        childCountUpdate = {
                          archivedItemCount:
                            existingUpdatedParent.archivedItemCount - 1,
                        };
                      } else {
                        childCountUpdate = {
                          itemCount: existingUpdatedParent.itemCount - 1,
                        };
                      }
                    } else {
                      childCountUpdate = {
                        folderCount: existingUpdatedParent.folderCount - 1,
                      };
                    }
                    updateWorkItems[existingUpdatedParentIndex] = {
                      ...existingUpdatedParent,
                      ...childCountUpdate,
                    };
                  } else {
                    if (item.itemType === ItemType.WORKITEM) {
                      if (item.archived) {
                        childCountUpdate = {
                          archivedItemCount:
                            (oldParent as PreparedFolder).archivedItemCount - 1,
                        };
                      } else {
                        childCountUpdate = {
                          itemCount:
                            (oldParent as PreparedFolder).itemCount - 1,
                        };
                      }
                    } else {
                      childCountUpdate = {
                        folderCount:
                          (oldParent as PreparedFolder).folderCount - 1,
                      };
                    }
                    updateWorkItems.push({
                      ...oldParent,
                      ...childCountUpdate,
                    });
                  }
                }
              }
              const newParent = cacheKeys
                .map((key) =>
                  this.itemsCaches[key].find(
                    (w: PreparedItem) => w.number === params.data.parentNumber
                  )
                )
                .find((i) => i);
              if (newParent) {
                const existingUpdatedParentIndex = updateWorkItems.findIndex(
                  (i) => i.number === newParent.number
                );
                let childCountUpdate: Partial<
                  Pick<
                    PreparedFolder,
                    'archivedItemCount' | 'itemCount' | 'folderCount'
                  >
                > = {};
                if (existingUpdatedParentIndex !== -1) {
                  const existingUpdatedParent = updateWorkItems[
                    existingUpdatedParentIndex
                  ] as PreparedFolder;
                  if (item.itemType === ItemType.WORKITEM) {
                    if (item.archived) {
                      childCountUpdate = {
                        archivedItemCount:
                          existingUpdatedParent.archivedItemCount - 1,
                      };
                    } else {
                      childCountUpdate = {
                        itemCount: existingUpdatedParent.itemCount - 1,
                      };
                    }
                  } else {
                    childCountUpdate = {
                      folderCount: existingUpdatedParent.folderCount + 1,
                    };
                  }
                  updateWorkItems[existingUpdatedParentIndex] = {
                    ...existingUpdatedParent,
                    ...childCountUpdate,
                  };
                } else {
                  if (item.itemType === ItemType.WORKITEM) {
                    if (item.archived) {
                      childCountUpdate = {
                        archivedItemCount:
                          (newParent as PreparedFolder).archivedItemCount - 1,
                      };
                    } else {
                      childCountUpdate = {
                        itemCount: (newParent as PreparedFolder).itemCount - 1,
                      };
                    }
                  } else {
                    childCountUpdate = {
                      folderCount:
                        (newParent as PreparedFolder).folderCount + 1,
                    };
                  }
                  updateWorkItems.push({
                    ...newParent,
                    ...childCountUpdate,
                  });
                }
              }
            }
          }

          if (!params.keyPatternsToAddTo) {
            this.addItems({
              items: updateWorkItems,
              disableAdding: true,
              keys: cacheKeys,
            });
          } else {
            cacheKeys.forEach((key) => {
              const patternMatchesKey = params.keyPatternsToAddTo?.some(
                (pattern) => key.includes(pattern)
              );
              if (patternMatchesKey) {
                this.addItems({
                  items: updateWorkItems,
                  keys: [key],
                });
              } else {
                this.addItems({
                  items: updateWorkItems,
                  disableAdding: true,
                  keys: [key],
                });
              }
            });
          }
          updateWorkItems.forEach((item) => {
            LocalEvents.emit('item-updated', item);
          });
        },
        {
          errorCallback: params.revertCallback,
        }
      );
    },
    async unarchiveItem(params: {
      itemNumber: string;
      itemType: ItemType;
      itemId: string;
      unarchiveChildren: boolean;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const response = await (params.itemType === ItemType.FOLDER
        ? ItemService.unarchiveFolder<true>({
            path: { organizationId, itemNumber: params.itemNumber },
            body: { unarchiveChildren: params.unarchiveChildren },
          })
        : ItemService.unarchiveWorkItem<true>({
            path: { organizationId, itemNumber: params.itemNumber },
            body: { unarchiveChildren: params.unarchiveChildren },
          }));
      const item = response.data.data;
      if (item && item.itemType === ItemType.WORKITEM) {
        this.addItems({
          items: [item as PreparedWorkItem],
        });
      } else if (item && item.itemType === ItemType.FOLDER) {
        this.addItems({
          items: [item as PreparedFolder],
        });
      }
    },
    async archiveWorkItem(params: {
      itemNumber: string;
      itemType: ItemType;
      itemId: string;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const response = await (params.itemType === ItemType.FOLDER
        ? ItemService.archiveFolder<true>({
            path: { organizationId, itemNumber: params.itemNumber },
          })
        : ItemService.archiveWorkItem<true>({
            path: { organizationId, itemNumber: params.itemNumber },
          }));
      const item = response.data.data;
      if (item && item.itemType === ItemType.WORKITEM) {
        this.addItems({
          items: [item as PreparedWorkItem],
        });
      } else if (item && item.itemType === ItemType.FOLDER) {
        this.addItems({
          items: [item as PreparedFolder],
        });
      }
    },
    async deleteWorkItem(params: {
      itemNumber: string;
      itemType: ItemType;
      itemId: string;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      await (params.itemType === ItemType.FOLDER
        ? ItemService.deleteFolder<true>({
            path: { organizationId, itemNumber: params.itemNumber },
          })
        : ItemService.deleteWorkItem<true>({
            path: { organizationId, itemNumber: params.itemNumber },
          }));

      const drawerItem = this.itemsCaches[ItemsCacheKeys.DRAWER_ITEMS]?.find(
        (i) => i.id === params.itemId
      );
      if (drawerItem) {
        this.toggleSideDrawerOpen(false);
      }
      this.workspaceItemNumbers = this.workspaceItemNumbers.filter(
        (i) => i !== params.itemNumber
      );
      this.removeItems({
        itemIds: [params.itemId],
      });
    },
    getRecentItemsAndFolders() {
      const lsRecentItems = getLocalStorageKey<Array<PreparedItem>>(
        orgScopedKey(LocalStorageKeys.RECENT_ITEMS)
      );
      if (lsRecentItems) {
        this.recentItems = lsRecentItems;
      }
    },
    setRecentItemsAndFolders(item: PreparedItem) {
      this.getRecentItemsAndFolders();
      this.recentItems = uniqBy([item, ...this.recentItems], 'id').slice(0, 20);
      setLocalStorageKey(
        orgScopedKey(LocalStorageKeys.RECENT_ITEMS),
        this.recentItems
      );
    },
    removeRecentItemsAndFolders(item: PreparedItem) {
      this.recentItems = this.recentItems.filter((i) => i.id !== item.id);
      setLocalStorageKey(
        orgScopedKey(LocalStorageKeys.RECENT_ITEMS),
        this.recentItems
      );
    },

    async updateAncestorCountsForNewItem(item: PreparedItem) {
      const addItems = [item];
      const allKeys = Object.keys(this.itemsCaches);
      for (const cacheKey of allKeys) {
        if (item.parentNumber) {
          const parent = this.itemsCaches[cacheKey].find(
            (w) => w.number === item.parentNumber
          ) as PreparedFolder | undefined;
          if (parent) {
            const existingParentIndex = addItems.findIndex(
              (i) => i.number === parent.number
            );
            let childCountUpdate: Partial<
              Pick<
                PreparedFolder,
                'archivedItemCount' | 'itemCount' | 'folderCount'
              >
            >;
            if (item.itemType === ItemType.WORKITEM) {
              if (item.archived) {
                childCountUpdate = {
                  archivedItemCount: parent.archivedItemCount + 1,
                };
              } else {
                childCountUpdate = {
                  itemCount: parent.itemCount + 1,
                };
              }
            } else {
              childCountUpdate = {
                folderCount: parent.folderCount + 1,
              };
            }
            if (existingParentIndex !== -1) {
              addItems[existingParentIndex] = {
                ...parent,
                ...childCountUpdate,
              };
            }
          }
        }
        const items = await prepareWorkItems(addItems);
        this.addItems({
          items,
          keys: [cacheKey],
        });
      }
    },

    async createItem({
      itemType,
      data,
    }:
      | {
          itemType: ItemType.WORKITEM;
          data: CreateWorkItem;
        }
      | {
          itemType: ItemType.FOLDER;
          data: CreateFolder;
        }) {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;

      const response = await (itemType === ItemType.WORKITEM
        ? ItemService.createWorkItem<true>({
            path: { organizationId: activeOrganization.id },
            body: data,
          })
        : ItemService.createFolder<true>({
            path: { organizationId: activeOrganization.id },
            body: data,
          }));

      const item = (await prepareWorkItems([response.data.data]))[0];
      // update the cache with the new item immediately
      this.addItems({ items: [item] });
      // Call Async so that we don't have to wait for the cache to update for related items
      this.updateAncestorCountsForNewItem(item);
      this.setRecentItemsAndFolders(item);

      LocalEvents.emit('item-created', item);
      return item;
    },
    async bulkCreateItems({
      data,
    }: {
      data: SubmitBulkCreateItemsJob['input'];
    }) {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;
      const jobsStore = useJobsStore();

      const response = await JobsService.submitBulkCreateItemsJob<true>({
        path: { organizationId: activeOrganization.id },
        body: {
          input: {
            ...data,
          },
        },
      });
      jobsStore.allJobs.push(response.data.data);
      return response.data.data;
    },
    async copyWorkItem(item: PreparedWorkItem, data: CopyWorkItem) {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;

      // create
      const response = await ItemService.copyWorkItem<true>({
        path: {
          organizationId: activeOrganization.id,
          itemNumber: item.number,
        },
        body: data,
      });
      const newItem = (await prepareWorkItems([response.data.data]))[0];

      // apply to drawer
      this.addItems({ items: [newItem], keys: [ItemsCacheKeys.DRAWER_ITEMS] });
      this.workspaceItemNumbers.push(newItem.number);
      this.toggleSideDrawerOpen(true);

      // update recents, emit and cleanup
      this.setRecentItemsAndFolders(newItem);
      LocalEvents.emit('item-created', newItem);

      // update related items asynchronously
      this.updateAncestorCountsForNewItem(newItem);

      return newItem;
    },

    async load({
      itemNumber,
      itemType,
      skipAddingCache,
      keys,
    }: {
      itemNumber: string;
      itemType: ItemType;
      skipAddingCache?: boolean;
      keys?: Array<string>;
      revertCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) {
        throw new Error('Not implemented');
      }

      let item: WorkItem | Folder | null = null;

      const itemResponse = await (itemType === ItemType.FOLDER
        ? ItemService.getFolderById<true>({
            path: { organizationId: activeOrganization.id, itemNumber },
          })
        : ItemService.getWorkItemById<true>({
            path: { organizationId: activeOrganization.id, itemNumber },
          }));
      item = itemResponse.data.data;

      if (!item) {
        throw new Error('Item not found');
      }
      const preparedWorkItem = (await prepareWorkItems([item]))[0];
      if (!skipAddingCache) {
        const items = await prepareWorkItems([item]);
        this.addItems({
          items,
          keys,
        });
      }
      this.setRecentItemsAndFolders(preparedWorkItem);
      return preparedWorkItem;
    },

    /** not guaranteed to return a fully populated item. Use only if you need to work with basic attributes like title, number, etc. */
    loadFromCache(number: PreparedItem['number']) {
      const existingItems = Object.values(this.itemsCaches).flat();
      const item = existingItems.find((i) => i.number === number) || null;
      return item;
    },

    async getItemHierarchy({
      itemNumber,
      keys,
    }: {
      itemNumber: string;
      keys?: Array<string>;
    }) {
      const organizationStore = useOrganizationStore();
      const organization = organizationStore.activeOrganization;
      if (!organization) return;
      const response = await ItemService.getItemHierarchy<true>({
        path: { organizationId: organization.id, itemNumber },
      });
      const preparedWorkItems = await prepareWorkItems(response.data.data);
      if (keys) {
        this.addItems({
          items: preparedWorkItems,
          keys,
        });
      }
      return preparedWorkItems;
    },

    async openSideDrawer({
      itemNumber,
      disableLoading,
    }: {
      itemNumber: string;
      disableLoading?: boolean;
    }) {
      if (
        this.workspaceItemNumbers.includes(itemNumber) &&
        this.workspaceItemNumbers[this.workspaceItemNumbers.length - 1] ===
          itemNumber
      ) {
        return;
      }
      this.workspaceItemNumbers.push(itemNumber);
      this.toggleSideDrawerOpen(true);
      if (!disableLoading) {
        this.toggleSideDrawerLoading(itemNumber, true);
      }
      const itemType = itemNumber.includes('F')
        ? ItemType.FOLDER
        : ItemType.WORKITEM;
      try {
        await this.load({
          itemNumber,
          itemType,
          keys: [ItemsCacheKeys.DRAWER_ITEMS],
        });
        this.toggleSideDrawerLoading(itemNumber, false);
      } catch (e) {
        const error = e as {
          response?: { status?: number; data?: { message?: string } };
          message?: string;
        };
        const errorResponse = error.response;
        const itemIsNotFound = errorResponse?.status === 404;
        if (!itemIsNotFound) console.error(e);
        this.toggleSideDrawerLoading(itemNumber, false);
        this.toggleSideDrawerOpen(false);
        this.workspaceItemNumbers = this.workspaceItemNumbers.filter(
          (i) => i !== itemNumber
        );
        let message =
          'There was an error loading the item drawer. If this persists, please contact support.';
        // Use the error message from the API response if available
        if (errorResponse?.data?.message) {
          message = errorResponse.data.message;
        } else if (error.message) {
          // Direct error message (when error is thrown directly)
          message = error.message;
        } else if (itemIsNotFound) {
          message = 'The item was not found. It may have been deleted.';
        }

        if (itemIsNotFound) {
          this.removeItems({
            itemIds: [itemNumber],
          });
        }

        Notify({
          type: 'error',
          message,
        });
      } finally {
        this.toggleSideDrawerAnimationDisabled(false);
      }
    },

    async createComment({ data }: { data: CreateItemComment }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await CommentService.addItemComment({
          path: {
            organizationId,
          },
          body: data,
        });
        return response.data;
      }) as Promise<CommentResponse>;
    },

    async createItemCommentReply({
      parentCommentId,
      data,
    }: {
      parentCommentId: string;
      data: CreateComment;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await CommentService.addCommentReply({
          path: {
            organizationId,
            commentId: parentCommentId,
          },
          body: data,
        });
        return response.data;
      }) as Promise<CommentResponse>;
    },

    async updateComment({
      commentId,
      body,
    }: {
      commentId: string;
      body: UpdateComment;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await CommentService.updateComment({
          path: {
            organizationId,
            commentId,
          },
          body,
        });
        return response.data;
      });
    },

    async getComment({ commentId }: { commentId: string }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await CommentService.getCommentById({
          path: {
            organizationId,
            commentId,
          },
        });
        return response.data;
      }) as Promise<CommentResponse>;
    },

    async getComments({ query }: { query: FindItemCommentQuery }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await CommentService.findItemComments({
          path: {
            organizationId,
          },
          query,
        });
        return response.data;
      }) as Promise<CommentListResponse>;
    },

    async loadCommentReplies({
      parentCommentId,
      query,
    }: {
      parentCommentId: string;
      query?: PaginatedQueryBase;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await CommentService.findCommentReplies({
          path: {
            organizationId,
            commentId: parentCommentId,
          },
          query,
        });
        return response.data;
      }) as Promise<CommentListResponse>;
    },

    async deleteComment(commentId: string) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        await CommentService.deleteComment({
          path: {
            organizationId,
            commentId,
          },
        });
      });
    },

    async addCommentReaction({
      commentId,
      reaction,
      revertCallback,
    }: {
      commentId: string;
      reaction: string;
      revertCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(
        async () => {
          await CommentService.addCommentReaction({
            path: {
              organizationId,
              commentId,
            },
            body: {
              reaction,
            },
          });
        },
        {
          errorCallback: revertCallback,
        }
      );
    },

    async removeCommentReaction({
      commentId,
      reaction,
      revertCallback,
    }: {
      commentId: string;
      reaction: string;
      revertCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(
        async () => {
          await CommentService.removeCommentReaction({
            path: {
              organizationId,
              commentId,
              reaction,
            },
          });
        },
        {
          errorCallback: revertCallback,
        }
      );
    },

    async setCommentAsFavorite({
      commentId,
      revertCallback,
    }: {
      commentId: string;
      revertCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(
        async () => {
          await CommentService.addCommentFavorite({
            path: {
              organizationId,
              commentId,
            },
          });
        },
        {
          errorCallback: revertCallback,
        }
      );
    },

    async removeCommentFromFavorite({
      commentId,
      revertCallback,
    }: {
      commentId: string;
      revertCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(
        async () => {
          await CommentService.removeCommentFavorite({
            path: {
              organizationId,
              commentId,
            },
          });
        },
        {
          errorCallback: revertCallback,
        }
      );
    },

    async createAssetGroup({
      itemNumber,
      itemType,
      data,
    }: {
      itemNumber: string;
      itemType: ItemType;
      data: CreateAssetGroup;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;

      return apiRequestHandler(async () => {
        const response = await AssetsService.createAssetGroup({
          path: {
            organizationId: organizationId,
            itemNumber: itemNumber,
            type: itemType === ItemType.WORKITEM ? 'work-items' : 'folders',
          },
          body: data,
        });
        return response.data.data;
      });
    },

    async deleteAssetGroup({
      itemNumber,
      itemType,
      groupId,
      revertCallback,
    }: {
      itemNumber: string;
      itemType: ItemType;
      groupId: string;
      revertCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(
        async () => {
          await AssetsService.deleteAssetGroup({
            path: {
              type: itemType === ItemType.WORKITEM ? 'work-items' : 'folders',
              organizationId: organizationId,
              itemNumber: itemNumber,
              assetGroupId: groupId,
            },
          });
        },
        { errorCallback: revertCallback }
      );
    },

    async updateAssetGroup({
      itemNumber,
      itemType,
      groupId,
      data,
      revertCallback,
    }: {
      itemNumber: string;
      itemType: ItemType;
      groupId: string;
      data: UpdateAssetGroup;
      revertCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(
        async () => {
          const response = await AssetsService.updateAssetGroup({
            path: {
              type: itemType === ItemType.WORKITEM ? 'work-items' : 'folders',
              organizationId: organizationId,
              itemNumber: itemNumber,
              assetGroupId: groupId,
            },
            body: data,
          });
          return response.data;
        },
        { errorCallback: revertCallback }
      );
    },

    async createAssetItem({
      itemNumber,
      itemType,
      data,
    }: {
      itemNumber: string;
      itemType: ItemType;
      data: CreateAssetItem;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await AssetsService.createAssetItem({
          path: {
            type: itemType === ItemType.WORKITEM ? 'work-items' : 'folders',
            organizationId: organizationId,
            itemNumber: itemNumber,
          },
          body: data,
        });

        return response.data;
      });
    },

    async deleteAssetItem({
      itemNumber,
      itemType,
      assetId,
      revertCallback,
    }: {
      itemNumber: string;
      itemType: ItemType;
      assetId: string;
      revertCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(
        async () => {
          await AssetsService.deleteAssetItem({
            path: {
              type: itemType === ItemType.WORKITEM ? 'work-items' : 'folders',
              organizationId: organizationId,
              itemNumber: itemNumber,
              assetItemId: assetId,
            },
          });
        },
        {
          errorCallback: revertCallback,
        }
      );
    },

    async updateAssetItem({
      itemNumber,
      itemType,
      assetId,
      data,
      revertCallback,
    }: {
      itemNumber: string;
      itemType: ItemType;
      assetId: string;
      data: UpdateAssetItem;
      revertCallback?: () => void;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(
        async () => {
          const response = await AssetsService.updateAssetItem({
            path: {
              type: itemType === ItemType.WORKITEM ? 'work-items' : 'folders',
              organizationId: organizationId,
              itemNumber: itemNumber,
              assetItemId: assetId,
            },
            body: data,
          });
          return response.data;
        },
        { errorCallback: revertCallback }
      );
    },

    async getItemAssets({
      number,
      itemType,
    }: {
      number: string;
      itemType: ItemType;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await AssetsService.findItemAssets({
          path: {
            type: itemType === ItemType.WORKITEM ? 'work-items' : 'folders',
            organizationId: organizationId,
            itemNumber: number,
          },
        });
        return response.data.data;
      });
    },

    async getPullRequests({ number }: { number: string }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response =
          await GithubIntegrationService.getWorkItemPullRequests<true>({
            path: {
              organizationId,
              itemNumber: number,
            },
          });
        return response.data.data;
      }) as Promise<PullRequest[]>;
    },

    async getBranches({ number }: { number: string }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;

      return apiRequestHandler(async () => {
        const response =
          await GithubIntegrationService.getWorkItemBranches<true>({
            path: {
              organizationId,
              itemNumber: number,
            },
          });
        return response.data.data;
      }) as Promise<Branch[]>;
    },

    async getCommits({ number }: { number: string }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response =
          await GithubIntegrationService.getWorkItemCommits<true>({
            path: {
              organizationId,
              itemNumber: number,
            },
          });
        return response.data.data;
      }) as Promise<Commit[]>;
    },

    async getPublicStoredViewItems({
      path: { storedViewId, organizationId },
      query,
    }: {
      path: {
        storedViewId: string;
        organizationId: string;
      };
      query?: {
        limit?: number;
        cursor?: string;
        direction?: 'asc' | 'desc';
        q?: string;
        sort?:
          | (
              | 'title'
              | 'authorUsername'
              | 'assigneeUsername'
              | 'boardRank'
              | 'parentRank'
              | 'laneRank'
              | 'workType'
              | 'priority'
              | 'lane'
              | 'team'
              | 'board'
              | 'subItemsCount'
              | 'labelsCount'
              | 'relationshipsCount'
              | 'dateAdded'
              | 'dateUpdated'
              | 'estimate'
              | 'pointEffort'
              | 'timeSpentSeconds'
              | 'startDate'
              | 'dueDate'
            )
          | string;
      };
    }) {
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await ItemService.getPublicStoredViewItems({
          path: { organizationId, storedViewId },
          query: {
            limit: query?.limit || 50,
            cursor: query?.cursor || '',
            direction: query?.direction || 'asc',
            q: query?.q || '',
            sort: query?.sort || 'dateAdded',
          },
        });
        return response.data;
      });
    },

    unlinkPullRequest({
      workItemId,
      pullRequestId,
    }: {
      workItemId: string;
      pullRequestId: string;
    }) {
      pullRequestId;
      workItemId;
      throw new Error('Not implemented');
    },

    reLinkPullRequest({
      workItemId,
      pullRequestId,
    }: {
      workItemId: string;
      pullRequestId: string;
    }) {
      pullRequestId;
      workItemId;
      throw new Error('Not implemented');
    },

    async getActivityLogs({
      workItemId,
      query,
    }: {
      workItemId: string;
      query: FindActivityLogsData['query'];
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;

      return apiRequestHandler(async () => {
        const response = await ActivityService.findActivityLogs<true>({
          path: { organizationId, referenceId: workItemId },
          query,
        });
        return response.data;
      });
    },

    async getActivityLogItemById(activityLogItemId: string) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;

      const response = await ActivityService.getActivityLogById<true>({
        path: { organizationId, activityLogId: activityLogItemId },
      });
      return response.data.data;
    },

    async exportItems(data: CreateExportItemsJob) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const dataModule = new DataModule(organizationId);
      return await dataModule.exportItems(data);
    },

    async findExportJobs(query: FindExportJobsQueryParams) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const dataModule = new DataModule(organizationId);
      return await dataModule.findExportJobs(query);
    },

    async getReportByExportJobId(exportJobId: string) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const dataModule = new DataModule(organizationId);
      return await dataModule.getReportByExportJobId(exportJobId);
    },

    async getExportJobById(exportJobId: string) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const dataModule = new DataModule(organizationId);
      return await dataModule.getExportJobById(exportJobId);
    },

    async deleteExportJobById(exportJobId: string) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      const dataModule = new DataModule(organizationId);
      return apiRequestHandler(() => {
        return dataModule.deleteExportByJobById(exportJobId);
      });
    },
    async createSubItem({
      itemNumber,
      data,
    }: {
      itemNumber: string;
      data: CreateSubItem;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await ItemService.createSubItem<true>({
          path: { organizationId, itemNumber },
          body: data,
        });
        await this.load({
          itemNumber,
          itemType: ItemType.WORKITEM,
        });
        return response.data.data;
      });
    },

    async updateSubItem({
      itemNumber,
      subItemId,
      data,
    }: {
      itemNumber: string;
      subItemId: string;
      data: UpdateSubItem;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await ItemService.updateSubItem<true>({
          path: { organizationId, itemNumber, subItemId },
          body: data,
        });
        await this.load({
          itemNumber,
          itemType: ItemType.WORKITEM,
        });
        return response.data.data;
      });
    },

    async deleteSubItem({
      itemNumber,
      subItemId,
    }: {
      itemNumber: string;
      subItemId: string;
    }) {
      const organizationStore = useOrganizationStore();
      const organizationId = organizationStore.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await ItemService.deleteSubItem<true>({
          path: { organizationId, itemNumber, subItemId },
        });
        await this.load({
          itemNumber,
          itemType: ItemType.WORKITEM,
        });
        return response.data;
      });
    },

    async addOrRemoveRelationshipGraphItem({
      item,
      action,
    }: {
      item: PreparedItem;
      action: 'add' | 'remove';
    }) {
      this.relationshipGraphItems = this.relationshipGraphItems.filter(
        (i) => i.number !== item.number
      );
      if (action === 'add') {
        this.relationshipGraphItems.push(item);
      }
    },
  },
});
